import { useParams, Link, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Calendar, BadgeCheck, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import { SubstackSlimFooter } from "@/components/SubstackLink";
import { TEAM, BOOKING_URL } from "@/team/teamData";
import { TeamPageHeader } from "@/TeamPage";

const AgentPage = () => {
  const { slug } = useParams();
  const agent = TEAM.find(m => m.slug === slug);
  if (!agent) return <Navigate to="/team" replace />;

  return (
    <div className="bg-white" data-testid={`agent-page-${agent.slug}`}>
      <TeamPageHeader />

      <section className="bg-[#0A192F] text-white py-16 md:py-24">
        <div className="max-w-[1100px] mx-auto px-4 md:px-8">
          <Link to="/team" className="inline-flex items-center gap-2 text-sm text-slate-300 hover:text-[#C5A059] transition-colors mb-10" data-testid="back-to-team-link">
            <ArrowLeft className="w-4 h-4" /> Back to Team
          </Link>
          <div className="grid md:grid-cols-[300px_1fr] gap-10 items-center">
            <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.7 }} className="mx-auto w-64 h-64 md:w-72 md:h-72 overflow-hidden rounded-3xl shadow-2xl relative">
              <img src={agent.image} alt={agent.name} className="w-full h-full object-cover object-top" data-testid="agent-photo" />
              <div className="absolute inset-0 rounded-3xl border border-[#C5A059]/40 ring-1 ring-inset ring-white/20" />
            </motion.div>
            <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.1 }}>
              <p className="text-sm tracking-[0.2em] uppercase text-[#C5A059] font-semibold mb-3">{agent.title}</p>
              <h1 className="text-4xl sm:text-5xl font-light tracking-tight mb-5" style={{ fontFamily: "'Cormorant Garamond', serif" }} data-testid="agent-name">
                {agent.name}
              </h1>
              {agent.credentials && (
                <div className="flex flex-wrap gap-2 mb-6">
                  {agent.credentials.map(c => (
                    <span key={c} className="inline-flex items-center gap-1.5 bg-[#C5A059]/15 border border-[#C5A059]/40 text-[#C5A059] rounded-full px-3 py-1 text-xs font-medium">
                      <BadgeCheck className="w-3.5 h-3.5" /> {c}
                    </span>
                  ))}
                </div>
              )}
              <div className="flex flex-wrap gap-3">
                <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer">
                  <Button className="btn-accent px-6 py-5 text-base" data-testid="agent-book-btn">
                    <Calendar className="w-4 h-4 mr-2" /> Book a Call with {agent.name.split(" ")[0]}
                  </Button>
                </a>
                {agent.link && (
                  <a href={agent.link} target="_blank" rel="noopener noreferrer">
                    <Button variant="outline" className="border-white/40 bg-transparent text-white hover:bg-white hover:text-[#0A192F] px-6 py-5 text-base" data-testid="agent-website-btn">
                      Personal Website <ExternalLink className="w-4 h-4 ml-2" />
                    </Button>
                  </a>
                )}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <section className="py-16 md:py-24">
        <div className="max-w-[1100px] mx-auto px-4 md:px-8 grid md:grid-cols-[1fr_320px] gap-12">
          <div data-testid="agent-bio">
            <h2 className="text-3xl font-light text-[#0A192F] mb-6" style={{ fontFamily: "'Cormorant Garamond', serif" }}>About {agent.name.split(",")[0]}</h2>
            {agent.longBio.map((p, i) => (
              <p key={i} className="text-[#57534E] text-base leading-relaxed mb-5">{p}</p>
            ))}
          </div>
          <aside>
            <div className="bg-[#F8F7F4] border border-[#E7E5E4] rounded-2xl p-8 sticky top-24" data-testid="agent-specialties">
              <p className="text-xs tracking-[0.2em] uppercase text-[#C5A059] font-semibold mb-4">Specialties</p>
              <ul className="space-y-3">
                {agent.specialties.map(s => (
                  <li key={s} className="flex items-start gap-2.5 text-sm text-[#0A192F]">
                    <BadgeCheck className="w-4 h-4 text-[#C5A059] flex-shrink-0 mt-0.5" /> {s}
                  </li>
                ))}
              </ul>
              <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer">
                <Button className="btn-accent w-full mt-8" data-testid="agent-sidebar-book-btn">
                  <Calendar className="w-4 h-4 mr-2" /> Schedule a Call
                </Button>
              </a>
            </div>
          </aside>
        </div>
      </section>

      <SubstackSlimFooter />
    </div>
  );
};

export default AgentPage;
